// Mirrors GPMetals.Core.Models.WasteTracking on the API.

export enum WasteReceiptStatus {
  Draft = 0,
  Failed = 1,
  Submitted = 2,
  Amended = 3,
  Excluded = 4,
}

export enum WasteReceiptCarrierType {
  OwnCarrier = 0,
  HouseholdDropOff = 1,
  ThirdPartyCarrier = 2,
}

export type WasteReceiptView = "pending" | "submitted" | "excluded";

/** Where the yard's consignment note photo is in its life. Mirrors WasteReceiptScanStatus on the API. */
export enum WasteReceiptScanStatus {
  None = 0,
  /** Photo filed, AI read in progress. */
  Pending = 1,
  /** AI read done; the office has not confirmed it yet. */
  AwaitingReview = 2,
  Confirmed = 3,
  /** Photo filed but the AI could not read it. */
  Failed = 4,
}

/** The carrier (haulier) block as the AI read it off the note. */
export interface IWasteReceiptScanCarrier {
  organisationName: string | null;
  registrationNumber: string | null;
  vehicleRegistration: string | null;
  address: string | null;
  postcode: string | null;
  phone: string | null;
  email: string | null;
  hasDetails: boolean;
}

export interface IWasteReceiptHaulierMatch {
  accountId: string;
  name: string | null;
  carrierRegistrationNumber: string | null;
  /** "registration" (exact carrier registration) or "name" (organisation name). */
  matchedBy: string | null;
}

export interface IWasteReceiptConsignmentScanResult {
  code: string | null;
  isValidFormat: boolean;
  /** high, medium, low or none. */
  confidence: string | null;
  notes: string | null;
  /** Digits only, e.g. 160104. */
  ewcCode: string | null;
  /** False when the note carries something other than 16 01 04; null when no code was read. */
  ewcCodeMatchesExpected: boolean | null;
  carrier: IWasteReceiptScanCarrier | null;
  carrierIsOwn: boolean;
  haulierMatch: IWasteReceiptHaulierMatch | null;
  suggestHaulierCreation: boolean;
}

/** Row field names (as on IWasteReceiptRow) the AI filled and the office has not yet confirmed. */
export type WasteReceiptAiField =
  | "hazardousWasteConsignmentCode"
  | "carrierType"
  | "haulierAccountId"
  | "carrierVehicleRegistration";

export interface IWasteReceiptConsignmentScan {
  status: WasteReceiptScanStatus;
  /** The WasteConsignmentNote document holding the photo; GET /vehicle/documents/{id}. */
  documentId: string | null;
  scannedOn: string | null;
  error: string | null;
  confirmedOn: string | null;
  confirmedBy: string | null;
  /** Null while pending or after a failed read. */
  result: IWasteReceiptConsignmentScanResult | null;
  aiFields: WasteReceiptAiField[];
}

export interface IWasteReceiptRow {
  vehicleId: string;
  leadId: string | null;
  receiptId: string | null;
  vrm: string | null;
  stockNumber: string | null;
  make: string | null;
  model: string | null;
  vin: string | null;
  collectedOn: string | null;
  isDropOff: boolean;
  contactName: string | null;
  vehiclePostcode: string | null;
  vehicleAddress: string | null;

  status: WasteReceiptStatus | null;

  dateTimeReceived: string | null;
  weightKg: number | null;
  weightIsEstimate: boolean;
  hazardousWasteConsignmentCode: string | null;
  carrierType: WasteReceiptCarrierType | null;
  carrierRegistrationNumber: string | null;
  carrierOrganisationName: string | null;
  carrierVehicleRegistration: string | null;
  recoveryCode: string | null;

  /** Linked haulier account; while set, carrier name and registration come from it. */
  haulierAccountId: string | null;
  haulierName: string | null;

  isReady: boolean;
  issues: string[];

  wasteTrackingId: string | null;
  warnings: string[];
  failureReason: string | null;
  submittedOn: string | null;
  submittedBy: string | null;
  amendedOn: string | null;

  /** Null until the yard has captured a consignment note photo for the vehicle. */
  consignmentScan: IWasteReceiptConsignmentScan | null;
}

/**
 * Editable fields. A single-row save sends the whole set (null clears a field); a bulk
 * patch only applies the fields that are not undefined/null.
 */
export interface IUpsertWasteReceiptDraft {
  dateTimeReceived?: string | null;
  useCollectedDateAsReceived?: boolean | null;
  weightKg?: number | null;
  weightIsEstimate?: boolean | null;
  hazardousWasteConsignmentCode?: string | null;
  carrierType?: WasteReceiptCarrierType | null;
  carrierRegistrationNumber?: string | null;
  carrierOrganisationName?: string | null;
  carrierVehicleRegistration?: string | null;
  recoveryCode?: string | null;
  /** Forces carrierType to ThirdPartyCarrier on the API; any other carrierType drops the link. */
  haulierAccountId?: string | null;
}

export interface IBulkWasteReceiptPatch {
  vehicleIds: string[];
  patch: IUpsertWasteReceiptDraft;
}

/** One vehicle's outcome from a consignment note code generation run. */
export interface IWasteReceiptGenerateResult {
  vehicleId: string;
  vrm: string | null;
  success: boolean;
  /** Left alone because it already had a code (or is already with DEFRA). */
  skipped: boolean;
  code: string | null;
  message: string | null;
}

export interface IWasteReceiptSubmitResult {
  vehicleId: string;
  vrm: string | null;
  success: boolean;
  skipped: boolean;
  wasteTrackingId: string | null;
  errors: string[];
  warnings: string[];
}
